import {
  Banner,
  BannerTypes,
  Button,
  Card,
  CardHeader,
  Checkbox,
  LoadingIndicator,
  Modal,
  Pill,
  Snackbar,
  SvgNames,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled/macro";
import { ModalButtonProp } from "@sunrun/experience-ui-components/lib/components/Modal";
import { useFlags } from "flagsmith/react";
import { useMediaQuery } from "react-responsive";
import { getEnvironment } from "@sunrun/sales-experience-shared";
import { useCapContext } from "../../providers/CapContext";
import { AutopayEnrollmentForm } from "../molecules/AutopayEnrollmentForm";
import { CapSectionTitle } from "../molecules/CapStepSection";
import { formatCurrency } from "../../../checkout/util/constants";
import { RadioCard } from "../../../checkout/components/atoms/RadioCard";
import { CapDoc, VivintEligibilityResponse } from "../../capTypes";
import { ContentLeft, ContentRight, Flex } from "../atoms/layoutAtoms";
import { SplatContact } from "../../../../amplify/backend/function/OfferExpContacts/ts/public/types";
import { postDocumentEmail } from "../../api/capApi";
import { useAppSelector } from "../../../store";
import { SlimContactCard } from "../../../checkout/components/molecules/SlimContactCard";
import { rollbar } from "../../../providers/rollbar";
import { postPaymentRequestEmail } from "../../../services/prospect";
import { checkVSHEligibility } from "../../../services/vivintEligibility";
import { useAuthToken } from "../../../hooks/useAuthToken";
import { shouldShowPostCapHomeCheckScheduler } from "../../constants/postCapHomeCheck";
import { shouldShowPostCapInstallScheduler } from "../../constants/capInstallScheduling";
import { shouldPollForRepRedesignCapDocs } from "../../utils/capRepRedesignDocRefresh";
import { CAP_DOCS_MAY_STILL_BE_UPDATING_MESSAGE } from "../../hooks/useCapRepRedesignDocPolling";
import { PostCapHomeCheckSection } from "./PostCapHomeCheckSection";
import { PostCapInstallSection } from "./PostCapInstallSection";
import { HyperlinkButton } from "components/atoms/HyperlinkButton";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";
import { FeatureFlags } from "fixtures/features";

const CAP_DOCS_NOT_AVAILABLE_MESSAGE = "Documents are not available";
const CAP_DOCS_NOT_GENERATED_USER_MESSAGE =
  "The CAP package has not yet been generated for this project. Please contact SSD if this is not correct";
const CAP_DOCS_LOAD_FAILED_USER_MESSAGE =
  "The CAP package could not be loaded. Please refresh the page to try again. If this error persists, please contact SSD for support";

const isCapDocsNotAvailableError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }
  const apiResponse = (
    error as { response?: { statusCode?: number; body?: unknown } }
  ).response;
  if (apiResponse?.statusCode === 404) {
    return true;
  }
  if (
    error instanceof Error &&
    error.message.includes(CAP_DOCS_NOT_AVAILABLE_MESSAGE)
  ) {
    return true;
  }
  const responseBody = apiResponse?.body;
  return (
    typeof responseBody === "string" &&
    responseBody.includes(CAP_DOCS_NOT_AVAILABLE_MESSAGE)
  );
};

const ReadSignStepView: React.FC = () => {
  const {
    sow,
    activeProposal,
    documents,
    documentsError,
    isDocumentsLoading,
    prospectId,
    prospect,
    contacts,
    isNonSolar,
    isCustomer,
    refetchCapDocuments,
    isDocumentsValidating,
    isPollingForNewCapDocs,
    capDocsMayStillBeUpdating,
    returnedFromRepRedesign,
  } = useCapContext();
  const authKey = useAppSelector((state) => state?.auth?.hybridToken);
  const proposalToken = useAppSelector((state) => state?.auth?.proposalToken);

  const authToken = useAuthToken("IHD");

  const [showAutopaySection, setShowAutopaySection] = useState(false);
  const [showBankForm, setShowBankForm] = useState(false);
  const [showAutopayForm, setShowAutopayForm] = useState(false);
  const [showAutopaySuccess, setShowAutopaySuccess] = useState(false);
  const [autopaySuccessResponse, setAutopaySuccessResponse] =
    useState(undefined);

  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  const [vivintData, setVivintData] = useState<
    VivintEligibilityResponse | undefined
  >();

  const flags = useFlags([
    FeatureFlags.VIVINT_SMART_HOME_UI,
    FeatureFlags.PPC_SCHEDULING_IN_ONE,
  ]);
  const canViewVivintSmartHome =
    flags[FeatureFlags.VIVINT_SMART_HOME_UI]?.enabled && !isNonSolar;
  const isPpcSchedulingInOneEnabled =
    flags[FeatureFlags.PPC_SCHEDULING_IN_ONE]?.enabled ?? false;

  const [paymentType, setPaymentType] = useState("echeck");
  const AUTOPAY_LINK = `https://www.sunrun.com/autopay`;
  const [documentError, setDocumentError] = useState<Error | undefined>(
    undefined
  );
  const [documentSendInProgress, setDocumentSendInProgress] = useState(false);
  const [documentToSend, setDocumentToSend] = useState<CapDoc | undefined>(
    undefined
  );
  const [showConfirmEmail, setShowConfirmEmail] = useState(false);
  const [showEmailSuccessToast, setShowEmailSuccessToast] = useState(false);
  const [consentToText, setConsentToText] = useState(false);
  const [showCapDocsErrorDetails, setShowCapDocsErrorDetails] = useState(false);

  useEffect(() => {
    if (prospectId && shouldPollForRepRedesignCapDocs(prospectId)) {
      return;
    }
    refetchCapDocuments?.();
  }, [prospectId, refetchCapDocuments]);

  const isDocumentsRefreshing =
    isPollingForNewCapDocs || isDocumentsLoading || isDocumentsValidating;

  const handleDocumentEmail = useCallback(async () => {
    const sendDocumentEmail = async (document: any, prospectId: string) => {
      const documentEmail = {
        primaryContactId:
          contacts?.filter((c: SplatContact) => c.primary)[0].contactId ?? "",
        customerSigned: document.customerSigned,
        documentType: document.documentType,
        srAttachmentId: document.documentId,
      };
      await postDocumentEmail(
        authKey,
        documentEmail,
        prospectId,
        proposalToken
      );
    };

    setDocumentSendInProgress(true);
    setShowConfirmEmail(false);

    try {
      if (documentToSend) {
        await sendDocumentEmail(documentToSend, prospectId ?? "");
        setShowEmailSuccessToast(true);
      }
    } catch (e) {
      setDocumentError(e as Error);
      resetValues();
    } finally {
      setDocumentSendInProgress(false);
    }
  }, [authKey, contacts, documentToSend, proposalToken, prospectId]);

  useEffect(() => {
    if (!authToken?.token || !prospectId || !canViewVivintSmartHome) {
      return;
    }
    checkVSHEligibility(authToken.token, prospectId)
      .then((vshData) => {
        setVivintData(vshData);
      })
      .catch((error) => {
        rollbar.error(error);
      });
  }, [authToken, prospectId, canViewVivintSmartHome]);

  const isAutopayEnrolled =
    sow?.billingMethod?.includes("Recurring") ||
    sow?.paymentMode?.includes("Recurring") ||
    autopaySuccessResponse;

  useEffect(() => {
    if (activeProposal) {
      if (
        activeProposal.dealType === "FlexMonthly PPA" ||
        activeProposal.dealType === "FlexMonthly" ||
        activeProposal.dealType === "monthly"
      ) {
        setShowAutopaySection(true);

        if (isAutopayEnrolled) {
          setShowAutopaySuccess(true);
        } else {
          setShowBankForm(true);
        }
      }
    }

    if (
      sow &&
      (sow.paymentMode === "Recurring CC" ||
        sow.billingMethod === "Recurring CC")
    ) {
      setPaymentType("card");
    }
  }, [
    isAutopayEnrolled,
    activeProposal,
    autopaySuccessResponse,
    paymentType,
    sow,
  ]);

  useEffect(() => {
    if (showAutopayForm) {
      setShowBankForm(false);
      setShowAutopaySuccess(false);
    }
  }, [showAutopayForm]);

  let monthlyPayment: string | undefined;
  if (activeProposal) {
    switch (activeProposal.dealType) {
      case "monthly":
      case "Monthly":
        monthlyPayment = formatCurrency(activeProposal.monthlySunrunBill);
        break;
      case "FlexMonthly":
        if (
          activeProposal.year1MinimumMonthlyBill &&
          activeProposal.year1MaximumMonthlyBill
        ) {
          monthlyPayment = `${formatCurrency(
            activeProposal.year1MinimumMonthlyBill
          )} - ${formatCurrency(activeProposal.year1MaximumMonthlyBill)}`;
        }
        break;
    }
  }

  const resetValues = () => {
    setDocumentToSend(undefined);
    setConsentToText(false);
    setShowConfirmEmail(false);
  };

  const handleCloseSendEmailSMSModal = () => {
    resetValues();
  };

  const confirmEmailProps: ModalButtonProp = {
    text: "Send",
    onClick: handleDocumentEmail,
    testId: "send-email-sms-modal-send-button",
    disabled: !consentToText,
  };

  const cancelEmailProps: ModalButtonProp = {
    text: "Cancel",
    onClick: handleCloseSendEmailSMSModal,
  };

  const readDocument = useCallback((doc: CapDoc) => {
    window.open(doc.documentLink, "_blank");
  }, []);

  const [confirmEmailAutopay, setConfirmEmailAutopay] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [showEmailSuccessSnackbar, setShowEmailSuccessSnackbar] =
    useState(false);

  const handleSendAutopayEmail = useCallback(async () => {
    setEmailLoading(true);
    let result;
    try {
      result = await postPaymentRequestEmail(authKey, prospectId!, "Recurring");
    } catch (e) {
      rollbar.error(e as Error);
    } finally {
      setConfirmEmailAutopay(false);
      setEmailLoading(false);
      if (result?.message === "Email sent successfully") {
        setShowEmailSuccessSnackbar(true);
      }
    }
  }, [authKey, prospectId]);

  const openVivintScheduler = () => {
    const url =
      getEnvironment() === "production"
        ? `https://designtools.sunrun.com/vsh/prospect/${prospectId}/scheduler`
        : `https://devdesigntools.sunrun.com/vsh/prospect/${prospectId}/scheduler`;

    window.open(url, "_blank");
  };

  const showVivintScheduler = vivintData?.eligibility?.isEligible ?? false;
  const docTextTitleSize = isTablet ? 20 : 24;
  const docTextDesciptionSize = isMobile ? 14 : 16;
  const vivintSmartHomeDiscount = prospect?.leadSource === "Vivint Smarthome";

  useEffect(() => {
    if (!!documentToSend) {
      setShowConfirmEmail(true);
    }
  }, [documentToSend]);

  const emailAutopayModalPrimaryButton = {
    text: "Send email",
    onClick: handleSendAutopayEmail,
    testId: "email-autopay-modal-send-button",
    ariaLabel: "email autopay modal send button",
    disabled: false,
  };

  const emailAutopayModalSecondaryButton = {
    text: "Cancel",
    onClick: () => setConfirmEmailAutopay(false),
    testId: "email-autopay-modal-cancel-button",
    ariaLabel: "email autopay modal cancel button",
    disabled: false,
  };

  return (
    <>
      <CapSectionTitle>Read & sign</CapSectionTitle>
      <AgreementsContainer>
        <Row showColumnStyle={isTablet} isFirstRowTabletView={isTablet}>
          <BaseSidebarText isTablet={isTablet}>
            <Typography variant="h4" size={docTextTitleSize}>
              Sign your documents
            </Typography>
            <Typography size={docTextDesciptionSize}>
              We are excited to work with you!
            </Typography>
          </BaseSidebarText>

          <BaseContainer isTablet={isTablet}>
            {isDocumentsRefreshing && <LoadingIndicator color="black" />}
            {capDocsMayStillBeUpdating && !isDocumentsRefreshing && (
              <Banner show showClose={false} bannerType={BannerTypes.InfoLight}>
                {CAP_DOCS_MAY_STILL_BE_UPDATING_MESSAGE}
              </Banner>
            )}
            {documentsError && (
              <Banner
                show
                showClose={false}
                bannerType={
                  isCapDocsNotAvailableError(documentsError)
                    ? BannerTypes.InfoLight
                    : BannerTypes.ErrorLight
                }
                secondaryAction={
                  isCapDocsNotAvailableError(documentsError)
                    ? undefined
                    : {
                        action: () => setShowCapDocsErrorDetails(true),
                        title: "Show Details",
                        testId: "cap-docs-show-details-button",
                      }
                }
              >
                {isCapDocsNotAvailableError(documentsError)
                  ? CAP_DOCS_NOT_GENERATED_USER_MESSAGE
                  : CAP_DOCS_LOAD_FAILED_USER_MESSAGE}
              </Banner>
            )}
            {!!documents?.length && prospect && !isDocumentsRefreshing && (
              <>
                {documents.map((doc: CapDoc, index) => {
                  return (
                    <CapDocument key={index}>
                      <CardHeader>
                        {isMobile ? (
                          <MobileCardHeaderContainer>
                            <Typography
                              color={tokens.BRAND_HERO_BLUE}
                              size={20}
                              fontWeight={"medium"}
                            >
                              {doc.documentType === "CAP Package"
                                ? "Design approval agreement"
                                : doc.documentType}
                            </Typography>
                            {doc.customerSigned === "Customer Signed" ? (
                              <StyledPill type="success" text="Signed" />
                            ) : (
                              <StyledPill
                                type="info"
                                icon={SvgNames.PenToolSvg}
                                text="Ready to Sign"
                              />
                            )}
                          </MobileCardHeaderContainer>
                        ) : (
                          <>
                            <ContentLeft>
                              <Typography
                                color={tokens.BRAND_HERO_BLUE}
                                size={20}
                                fontWeight={"medium"}
                              >
                                {doc.documentType === "CAP Package"
                                  ? "Design approval agreement"
                                  : doc.documentType}
                              </Typography>
                            </ContentLeft>
                            {doc.customerSigned === "Customer Signed" ? (
                              <ContentRight>
                                <Pill type="success" text="Signed"></Pill>
                              </ContentRight>
                            ) : (
                              <ContentRight>
                                <Pill
                                  type="info"
                                  icon={SvgNames.PenToolSvg}
                                  text="Ready to Sign"
                                />
                              </ContentRight>
                            )}
                          </>
                        )}
                      </CardHeader>

                      {doc.documentType === "CAP Package" && (
                        <Typography>
                          {doc.customerSigned !== "Customer Signed" &&
                          returnedFromRepRedesign ? (
                            <>
                              Please review and sign your solar agreement with
                              Sunrun.
                              <br />
                              If you just signed the agreement and the status
                              isn&apos;t updated, please{" "}
                              <HyperlinkButton
                                onClick={() => {
                                  window.location.reload();
                                }}
                              >
                                refresh this page
                              </HyperlinkButton>
                              .
                            </>
                          ) : (
                            "Please review the terms of your solar agreement with Sunrun"
                          )}
                        </Typography>
                      )}
                      <Flex>
                        <ContentLeft>
                          {doc.customerSigned !== "Customer Signed" && (
                            <UnderlinedButton onClick={() => readDocument(doc)}>
                              <Typography
                                size={16}
                                lineHeight={24}
                                fontWeight={"medium"}
                              >
                                Read
                              </Typography>
                            </UnderlinedButton>
                          )}
                        </ContentLeft>
                        <ContentRight>
                          {doc.customerSigned === "Customer Signed" ? (
                            <SignedDocButton
                              color={"secondary"}
                              onClick={() => readDocument(doc)}
                            >
                              {documentSendInProgress &&
                              documentToSend === doc ? (
                                <LoadingIndicator showLoadingMessage={false} />
                              ) : (
                                <span>View signed document</span>
                              )}
                            </SignedDocButton>
                          ) : (
                            <RoundedButton
                              key={`email-text-button-${doc.documentId}`}
                              data-testid={`email-text-button-${doc.documentId}`}
                              disabled={documentSendInProgress}
                              onClick={() => setDocumentToSend(doc)}
                            >
                              {documentSendInProgress &&
                              documentToSend === doc ? (
                                <LoadingIndicator showLoadingMessage={false} />
                              ) : (
                                <span>Email & text</span>
                              )}
                            </RoundedButton>
                          )}
                        </ContentRight>
                      </Flex>
                    </CapDocument>
                  );
                })}

                {showConfirmEmail && (
                  <SendEmailSMSModal
                    customModalWidth={820}
                    title="Confirm contact information"
                    subtitle="The design approval agreement will be sent to the contact below"
                    testId="send-email-sms-modal"
                    primaryButton={confirmEmailProps}
                    secondaryButton={cancelEmailProps}
                    type="info"
                    hideIcon={true}
                    onClose={handleCloseSendEmailSMSModal}
                    closeButtonTestId="send-email-sms-modal-close-button"
                  >
                    <SendEmailSMSContent>
                      <Typography style={SendEmailSMSCustomerName}>
                        {`${prospect?.customerFirstName} ${prospect?.customerLastName}`}
                      </Typography>
                      <SendEmailSMSCustomerInfo>
                        <div>
                          <Typography style={SendEmailSMSCustomerHeadInfoItem}>
                            Phone
                          </Typography>
                          <Typography style={SendEmailSMSCustomerLabInfoItem}>
                            {prospect?.customerPrimaryPhone}
                          </Typography>
                        </div>
                        <div>
                          <Typography style={SendEmailSMSCustomerHeadInfoItem}>
                            Email
                          </Typography>
                          <Typography style={SendEmailSMSCustomerLabInfoItem}>
                            {prospect?.customerEmail}
                          </Typography>
                        </div>
                      </SendEmailSMSCustomerInfo>
                    </SendEmailSMSContent>
                    <Checkbox
                      id="consent-to-text-checkbox"
                      role="checkbox"
                      label="Customer gave verbal consent to receive this text message"
                      style={{ color: tokens.BRAND_HERO_BLUE }}
                      onChange={(e) => setConsentToText(e.target.checked)}
                    />
                  </SendEmailSMSModal>
                )}
                {documentError && (
                  <Modal
                    title="Error sending document email"
                    type="error"
                    testId="send-email-sms-modal-error"
                    onClose={() => setDocumentError(undefined)}
                  >
                    <div>{documentError.message}</div>
                  </Modal>
                )}
              </>
            )}
          </BaseContainer>
        </Row>
        {showCapDocsErrorDetails && documentsError && (
          <Modal
            title="Cap Documents not available"
            type="error"
            testId="cap-docs-error-details-modal"
            onClose={() => setShowCapDocsErrorDetails(false)}
          >
            <div>
              {documentsError instanceof Error
                ? documentsError.message
                : String(documentsError)}
            </div>
          </Modal>
        )}
        {!!sow?.priceToCustomer && !isCustomer && (
          <Row
            className="sow-financing-row"
            showColumnStyle={isTablet}
            isFirstRowTabletView={false}
          >
            <BaseSidebarText isTablet={isTablet}>
              <Typography variant="h4" size={docTextTitleSize}>
                Optional Home Upgrade Loan
              </Typography>
              <Typography size={docTextDesciptionSize}>
                For customers who want to finance their home upgrade costs and
                said yes to financing in their design approval agreement.
              </Typography>
            </BaseSidebarText>
            <BaseContainer isTablet={isTablet}>
              <Card>
                <CardHeader>
                  <Typography size={19} fontWeight={"medium"}>
                    Optional Home Upgrade Loan
                  </Typography>
                </CardHeader>
                <Typography>
                  Your sales rep will fill out a form and then send the loan
                  documents to you for signing. To cover your home upgrade costs
                  of ${sow?.priceToCustomer}
                </Typography>
                <Row
                  style={{ justifyContent: "flex-end" }}
                  showColumnStyle={isTablet}
                  isFirstRowTabletView={false}
                >
                  <ContinueAutopayButton
                    isMobile={isMobile}
                    color={"primary"}
                    onClick={() =>
                      window.open(
                        "https://www.goodleap.dev/sunrun/08bd442a-0503-4a27-b83f-0427ab38f8c0",
                        "_blank"
                      )
                    }
                  >
                    Prepare loan docs
                  </ContinueAutopayButton>
                </Row>
              </Card>
            </BaseContainer>
          </Row>
        )}
        {showVivintScheduler &&
          canViewVivintSmartHome &&
          !vivintSmartHomeDiscount && (
            <Row showColumnStyle={isTablet} isFirstRowTabletView={false}>
              <BaseSidebarText isTablet={isTablet}>
                <Typography variant="h4" size={docTextTitleSize}>
                  Get Vivint Smart Home
                </Typography>
                <Typography size={docTextDesciptionSize}>
                  You qualified to get a free Vivint Smart Home with your Sunrun
                  system. Schedule an installation to get started.
                </Typography>
              </BaseSidebarText>
              <BaseContainer isTablet={isTablet}>
                <Card>
                  <CardHeader>
                    <Typography size={19} fontWeight="medium">
                      Vivint Smart Home installation
                    </Typography>
                  </CardHeader>

                  <Typography>
                    A technician will visit your home to install your Vivint
                    Smart Home.
                  </Typography>

                  <Flex>
                    <Button
                      color="link"
                      onClick={() =>
                        window.open(
                          "https://s.tiled.co/2G6ONl5/vivint-fast-pitch-2025",
                          "_blank"
                        )
                      }
                    >
                      Vivint Details
                    </Button>
                    <RoundedButton
                      style={{ marginLeft: "auto" }}
                      disabled={false}
                      onClick={openVivintScheduler}
                    >
                      Schedule installation
                    </RoundedButton>
                  </Flex>
                </Card>
              </BaseContainer>
            </Row>
          )}
        {vivintSmartHomeDiscount && (
          <Row showColumnStyle={isTablet} isFirstRowTabletView={false}>
            <BaseSidebarText isTablet={isTablet}>
              <Typography variant="h4" size={docTextTitleSize}>
                Vivint Smart Home
              </Typography>
              <Typography size={docTextDesciptionSize}>
                Thanks to our partnership with Vivint, you're eligible for an
                exclusive discount on your existing Vivint Smart Home.
              </Typography>
            </BaseSidebarText>
            <BaseContainer isTablet={isTablet}>
              <Card>
                <CardHeader>
                  <Typography size={19} fontWeight="medium">
                    Vivint Smart Home monthly discount
                  </Typography>
                </CardHeader>
                <Typography>
                  Vivint Monthly discount will be auto applied for the customer
                  at Solar CAP Approved.
                </Typography>
              </Card>
            </BaseContainer>
          </Row>
        )}

        {showAutopaySection && (
          <Row
            className="autopay-row"
            showColumnStyle={isTablet}
            isFirstRowTabletView={false}
          >
            <BaseSidebarText isTablet={isTablet}>
              <Typography variant="h4" size={docTextTitleSize}>
                Enroll in autopay
              </Typography>
              <Typography size={docTextDesciptionSize}>
                Your quoted first year monthly bill includes the discount you
                receive when you sign up for ACH autopay.
              </Typography>
              <div />
              <Typography size={docTextDesciptionSize}>
                For exact discount details, please refer to your customer
                agreement. If you have questions, please visit{" "}
                <a
                  href={AUTOPAY_LINK}
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  www.sunrun.com/autopay
                </a>
              </Typography>
            </BaseSidebarText>
            {prospect && contacts && (
              <BaseContainer isTablet={isTablet}>
                {!isAutopayEnrolled && showBankForm && (
                  <Card>
                    <CardHeader>
                      <Typography size={19} fontWeight={"medium"}>
                        Autopay Details
                      </Typography>
                    </CardHeader>
                    <SmashedPumpkins>
                      <Typography
                        size={16}
                        fontWeight={"medium"}
                        color={tokens.BRAND_HERO_BLUE}
                      >
                        Autopay amount
                      </Typography>
                      <Typography
                        size={28}
                        fontWeight={"medium"}
                        color={tokens.BRAND_HERO_BLUE}
                      >
                        {monthlyPayment}/mo
                      </Typography>
                      <Typography
                        size={14}
                        color={tokens.OFFWHITE_10}
                        fontWeight={"regular"}
                      >
                        Price includes discount for enrolling in ACH Autopay
                      </Typography>
                    </SmashedPumpkins>
                    {!showAutopaySuccess && showBankForm && (
                      <FlexGap>
                        <Typography color={tokens.BRAND_HERO_BLUE}>
                          Payment Method
                        </Typography>
                        <RadioCard
                          labelElement={
                            <LabelContainer role="label" isMobile={isMobile}>
                              <Typography
                                size={tokens.FONT_SIZE_3}
                                color={tokens.BRAND_HEROBLUE}
                                fontWeight="medium"
                              >
                                Checking
                              </Typography>
                              <Typography
                                color={tokens.TINTS_OFF_WHITE_20}
                                size={
                                  isMobile
                                    ? tokens.FONT_SIZE_1
                                    : tokens.FONT_SIZE_3
                                }
                              >
                                Recurring bank transfer
                              </Typography>
                            </LabelContainer>
                          }
                          checked={paymentType === "echeck"}
                          handleChange={() => setPaymentType("echeck")}
                        />
                        <RadioCard
                          labelElement={
                            <LabelContainer role="label" isMobile={isMobile}>
                              <Typography
                                size={tokens.FONT_SIZE_3}
                                color={tokens.BRAND_HEROBLUE}
                                fontWeight="medium"
                              >
                                Savings
                              </Typography>
                              <Typography
                                color={tokens.TINTS_OFF_WHITE_20}
                                size={
                                  isMobile
                                    ? tokens.FONT_SIZE_1
                                    : tokens.FONT_SIZE_3
                                }
                              >
                                Recurring bank transfer
                              </Typography>
                            </LabelContainer>
                          }
                          checked={paymentType === "savings"}
                          handleChange={() => setPaymentType("savings")}
                        />
                        <Row
                          style={{ justifyContent: "flex-end" }}
                          showColumnStyle={isMobile}
                          isFirstRowTabletView={false}
                        >
                          <EmailAutopayButton
                            isMobile={isMobile}
                            color={"secondary"}
                            onClick={() => setConfirmEmailAutopay(true)}
                          >
                            Email autopay
                          </EmailAutopayButton>
                          <ContinueAutopayButton
                            isMobile={isMobile}
                            onClick={() => setShowAutopayForm(true)}
                          >
                            Continue with autopay
                          </ContinueAutopayButton>
                        </Row>
                      </FlexGap>
                    )}
                    {confirmEmailAutopay && (
                      <Modal
                        title={"Email autopay form"}
                        type={"info"}
                        hideIcon={true}
                        onClose={() => setConfirmEmailAutopay(false)}
                        primaryButton={emailAutopayModalPrimaryButton}
                        secondaryButton={emailAutopayModalSecondaryButton}
                        testId="email-autopay-modal"
                      >
                        {emailLoading ? (
                          <LoadingIndicator
                            color={"black"}
                            showLoadingMessage={false}
                          />
                        ) : (
                          <>
                            <Typography
                              style={{ marginBottom: 24 }}
                              size={20}
                              lineHeight={26}
                            >
                              The autopay form will be sent to the email address
                              below.
                            </Typography>
                            <SlimContactCard
                              showEmail={true}
                              contact={
                                contacts.find((c) => c.primary) ?? contacts[0]
                              }
                            />
                          </>
                        )}
                      </Modal>
                    )}
                  </Card>
                )}
                <AutopayEnrollmentForm
                  autopayPaymentAmount={monthlyPayment?.toString() ?? ""}
                  prospect={prospect}
                  contacts={contacts}
                  paymentType={paymentType}
                  showForm={showAutopayForm}
                  setShowForm={setShowAutopayForm}
                  showBankForm={showBankForm}
                  showSuccess={showAutopaySuccess}
                  setSuccessResponse={(data) => setAutopaySuccessResponse(data)}
                  hideExtraPadding={true}
                />
              </BaseContainer>
            )}
          </Row>
        )}
        {shouldShowPostCapHomeCheckScheduler(
          sow?.otherCapReqs,
          sow?.postCapHomeCheckNeeded,
          isPpcSchedulingInOneEnabled
        ) && (
          <PostCapHomeCheckSection
            docTextTitleSize={docTextTitleSize}
            docTextDescriptionSize={docTextDesciptionSize}
            isTablet={isTablet}
          />
        )}
        {shouldShowPostCapInstallScheduler(prospect) && (
          <PostCapInstallSection
            docTextTitleSize={docTextTitleSize}
            docTextDescriptionSize={docTextDesciptionSize}
            isTablet={isTablet}
          />
        )}
        {vivintData &&
          !vivintData?.eligibility?.isEligible &&
          !isNonSolar &&
          !vivintSmartHomeDiscount && (
            <Row showColumnStyle={isTablet} isFirstRowTabletView={false}>
              <BaseSidebarText isTablet={isTablet} />
              <BaseContainer isTablet={isTablet}>
                <Banner
                  show
                  showClose={false}
                  title="Vivint Smart Home not eligible for this customer"
                >
                  {vivintData?.eligibility?.zipcodeIsEligble
                    ? "Not eligible due to previous relationship on record with VSH"
                    : "Not eligible due to zip code"}
                </Banner>
              </BaseContainer>
            </Row>
          )}
        <SnackbarOverlay>
          <Snackbar
            type="success"
            colorScheme="dark"
            open={showEmailSuccessToast}
            onClose={() => {
              setShowEmailSuccessToast(false);
              setDocumentToSend(undefined);
            }}
            message={`${
              documentToSend?.documentType === "CAP Package"
                ? "Design approval agreement"
                : documentToSend?.documentType
            } sent successfully`}
          />
          <Snackbar
            open={showEmailSuccessSnackbar}
            autoHideDuration={4000}
            colorScheme="dark"
            message=""
            onClose={() => setShowEmailSuccessSnackbar(false)}
            type="success"
          >
            <Typography color={tokens.SUCCESS_90} variant="span">
              Autopay form sent successfully!
              <br />
              Check your inbox or spam folder. You can also enroll through the
              Sunrun Customer App.
            </Typography>
          </Snackbar>
        </SnackbarOverlay>
      </AgreementsContainer>
    </>
  );
};

const BaseSidebarText = styled.div<{ isTablet: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ isTablet }) => (isTablet ? "100%" : "30%")};
  gap: ${({ isTablet }) => (isTablet ? "4px" : "12px")};
`;

const UnderlinedButton = styled(Button)`
  background: unset;
  border: 0;
  border-radius: unset;
  color: ${tokens.BRAND_HERO_BLUE};
  border-bottom: 2px solid ${tokens.BRAND_HERO_BLUE};
  padding: 0;
`;

const FlexGap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EmailAutopayButton = styled(Button)<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? "unset" : "fit-content")};
  padding: 10px 48px !important;
`;

const ContinueAutopayButton = styled(Button)<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? "unset" : "fit-content")};
  padding: 10px 48px;
`;

const CapDocument = styled(Card)``;

const Row = styled.div<{
  showColumnStyle: boolean;
  isFirstRowTabletView: boolean;
}>`
  padding-top: ${({ isFirstRowTabletView }) =>
    isFirstRowTabletView ? "12px" : "40px"};
  display: flex;
  flex-direction: ${({ showColumnStyle }) =>
    showColumnStyle ? "column" : "row"};
  gap: 12px;
`;

const RoundedButton = styled(Button)`
  padding: 8px 48px;
`;

const SignedDocButton = styled(Button)`
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  padding: 4px 12px !important; //yes important is required here to overwrite the secondary button's styles
`;

const BaseContainer = styled.div<{ isTablet: boolean }>`
  width: ${({ isTablet }) => (isTablet ? "100%" : "70%")};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MobileCardHeaderContainer = styled.div`
  padding: 4px;
`;

const StyledPill = styled(Pill)`
  padding: 0px 6px !important;
  min-height: 19px !important;
  height: 19px;
  margin-top: 8px;

  > p {
    font-size: 10px;
  }

  > div {
    > svg {
      width: 13px;
    }
  }
`;

const SmashedPumpkins = styled(Card)`
  border: unset;

  & > p {
    margin: unset !important;
  }
`;

const LabelContainer = styled.div<{ isMobile: boolean }>`
  gap: ${({ isMobile }) => (isMobile ? "0" : "8px")};
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? "column" : "unset")};
`;

const AgreementsContainer = styled.section``;

const SnackbarOverlay = styled.div`
  position: relative;
  z-index: 10000;
`;

const SendEmailSMSModal = styled(Modal)`
  [data-testid="modal-header"] h1 {
    font-size: 44px !important;
  }
  [data-testid="modal-header"] p {
    font-size: 20px !important;
    line-height: 130%;
    letter-spacing: -0.4px;
    color: ${tokens.BRAND_HERO_BLUE};
  }
  > div:first-of-type {
    /* ModalBackground override */
    background-color: rgba(31, 38, 71, 0.56) !important;
  }
`;

const SendEmailSMSContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  background: ${tokens.TINTS_OFF_WHITE_80};
  padding: 20px 24px;
  margin-bottom: 24px;
  gap: 16px;
`;

const SendEmailSMSCustomerInfo = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
`;

const SendEmailSMSCustomerName = {
  fontWeight: 500,
  fontSize: 16,
  lineHeight: "120%",
  marginBottom: 0,
  color: tokens.BRAND_HERO_BLUE,
};

const SendEmailSMSCustomerHeadInfoItem = {
  fontSize: 16,
  fontWeight: 400,
  lineHeight: "140%",
  color: tokens.TINTS_OFF_WHITE_20,
};

const SendEmailSMSCustomerLabInfoItem = {
  ...SendEmailSMSCustomerHeadInfoItem,
  color: tokens.BRAND_HEROBLUE,
  wordBreak: "break-word" as any,
};

export { ReadSignStepView };
